import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { formatDate, formatDateTime } from '@/utils/dateFormat';
import { StatusBadge } from '@/components/ui/status-badge';
import { ShieldCheck, Calendar, User, Building, Thermometer, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConsultaPublicaPage() {
  const { cicloId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCiclo();
  }, [cicloId]);

  const fetchCiclo = async () => {
    try {
      const response = await axios.get(`${API}/pdf/consulta/${cicloId}`);
      setData(response.data);
    } catch (error) {
      setError('Ciclo não encontrado');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Carregando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="text-red-600 text-xl font-semibold mb-2">Erro</div>
          <div className="text-slate-600">{error}</div>
        </div>
      </div>
    );
  }

  const { ciclo, autoclave, profissional, cliente, materiais, caixas } = data;
  const isValid = new Date(ciclo.data_validade) > new Date();

  return (
    <div data-testid="consulta-page" className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-lg mb-4">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Consulta de Esterilização
          </h1>
          <p className="text-lg text-slate-600 mt-2">Sterisul - Centro de Esterilização</p>
        </div>

        {/* Main Card */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          {/* Status Banner */}
          <div className={`p-4 text-center ${
            ciclo.resultado === 'aprovado' 
              ? isValid ? 'bg-green-50 border-b border-green-200' : 'bg-red-50 border-b border-red-200'
              : ciclo.resultado === 'reprovado' 
              ? 'bg-red-50 border-b border-red-200'
              : 'bg-indigo-50 border-b border-indigo-200'
          }`}>
            <div className="flex items-center justify-center gap-2">
              <StatusBadge status={ciclo.resultado} />
              {ciclo.resultado === 'aprovado' && (
                <StatusBadge status={isValid ? 'ativo' : 'vencido'} />
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Cycle Info */}
            <div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Ciclo {ciclo.numero_ciclo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Data de Esterilização</div>
                    <div className="text-sm text-slate-900 mt-1">
                      {formatDateTime(ciclo.data_hora)}
                    </div>
                    {(ciclo.hora_inicio || ciclo.hora_fim) && (
                      <div className="text-xs text-slate-600 mt-1">
                        {ciclo.hora_inicio && <span>Início: <b>{ciclo.hora_inicio}</b></span>}
                        {ciclo.hora_inicio && ciclo.hora_fim && <span className="mx-1">|</span>}
                        {ciclo.hora_fim && <span>Fim: <b>{ciclo.hora_fim}</b></span>}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Validade</div>
                    <div className="text-sm text-slate-900 mt-1">
                      {formatDate(ciclo.data_validade)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Client */}
            {cliente && (
              <div className="border-t border-slate-200 pt-6">
                <div className="flex items-start gap-3">
                  <Building className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Cliente</div>
                    <div className="text-sm text-slate-900 mt-1">{cliente.nome}</div>
                    <div className="text-xs text-slate-600 mt-1">{cliente.tipo}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Autoclave */}
            {autoclave && (
              <div className="border-t border-slate-200 pt-6">
                <div className="flex items-start gap-3">
                  <Thermometer className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Equipamento</div>
                    <div className="text-sm text-slate-900 mt-1">
                      {autoclave.identificacao} - {autoclave.marca} {autoclave.modelo}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      Método: {ciclo.metodo} | Temp: {ciclo.temperatura}°C | Pressão: {ciclo.pressao} bar | Tempo: {ciclo.tempo_minutos} min
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Professional */}
            {profissional && (
              <div className="border-t border-slate-200 pt-6">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Profissional Responsável</div>
                    <div className="text-sm text-slate-900 mt-1">{profissional.name}</div>
                    <div className="text-xs text-slate-600 mt-1">{profissional.role}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Caixas Cirúrgicas */}
            {caixas && caixas.length > 0 && (
              <div className="border-t border-slate-200 pt-6">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Caixas Cirúrgicas</div>
                <div className="space-y-3">
                  {caixas.map((caixa, idx) => (
                    <div key={idx} className="border border-blue-100 rounded-lg overflow-hidden">
                      <div className="bg-blue-50 px-4 py-2 flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-900">{caixa.nome}</span>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{caixa.total_pecas} peça(s)</span>
                      </div>
                      <div className="px-4 py-2 space-y-1">
                        {caixa.itens?.map((item, iIdx) => (
                          <div key={iIdx} className="flex justify-between text-sm text-slate-700">
                            <span>{item.codigo} — {item.nome}</span>
                            <span className="font-medium">x{item.quantidade}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Materials */}
            {materiais && materiais.length > 0 && (
              <div className="border-t border-slate-200 pt-6">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Materiais Esterilizados</div>
                <div className="space-y-2">
                  {materiais.map((material) => (
                    <div key={material.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{material.nome}</div>
                        <div className="text-xs text-slate-600">Código: {material.codigo} | Categoria: {material.categoria}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        {material.quantidade && material.quantidade !== '-' && (
                          <span className="text-xs font-medium text-slate-700 bg-slate-200 px-2 py-1 rounded">
                            Qtd: {material.quantidade}
                          </span>
                        )}
                        <StatusBadge status={material.status} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>Sterisul - Centro de Esterilização</p>
          <p className="mt-1">Rastreabilidade e conformidade ANVISA/RDC 15</p>
        </div>
      </div>
    </div>
  );
}